import { IEntity } from '@Data/entities/Interfaces';

export enum QualityControlState {
    PENDING,
    IN_PROGRESS,
}

export enum AnalysisEntityState {
    TODO,
    PASS,
    REWORK,
}

export interface IReworkReasonStage {
    id: number;
    description: string;
    topics: IReworkReasonTopic[];
}

export interface IReworkReasonTopic {
    id: number;
    description: string;
    reasons: IReworkReason[];
    fk_stage: number;
}

export interface IReworkReason {
    id: number;
    description: string;
    comment?: string;
    fk_topic: number;
}

export type IAnalysisEntity = Partial<Omit<IEntity, 'client_id'>> & {
    entity_version_id: string;
    qcState: AnalysisEntityState;
    reworkReasons: IReworkReason[];
    client_id: string;
    hasBaseData: boolean; // a flag to determine whether we have loaded entity version data
};

export enum TableSortField {
    NONE,
    CLIENT_ID,
    QC_STATE,
}

export interface IQcBaseEntity {
    photoPosition: QcPhotoPosition;
    truesizeImageUrls: string[];
    truesizeImageInfo: ITruesizeImage[];
    backwardLabels: Array<ISvgLabelData>;
    forwardLabels: Array<ISvgLabelData>;
    title?: string;
    bearing?: number;
    distance?: string;
}

export interface ITruesizeImage {
    url: string;
    resolution: { width: number; height: number };
    bearing: number;
    lat?: number;
    lng?: number;
}

export type SvgLabelType =
    | 'agl'
    | 'wire'
    | 'equipment'
    | 'mre'
    | 'anchor'
    | 'nonattached'
    | 'guy'
    | 'spanGuy'
    | 'dropspan'
    | 'generic';

export interface ISvgLabelData {
    id: string;
    type?: SvgLabelType;
    point_type?: string;
    x?: number;
    y?: number;
    height: number;
    name: string;
    labelY?: number;
    dropdownData?: ISvgLabelDropdownData[];
    offset?: number;
    index?: number; // used for no height labels
}

export interface ISvgLabelDropdownData {
    name: string;
    value?: string | number | React.ReactElement;
}

export enum QcPhotoPosition {
    BACKPOLE,
    BACKSPAN,
    CENTREPOLE,
    FORESPAN,
    FOREPOLE,
}

export interface IPoletagImage {
    url: string;
    image_id: string;
    collection_field_name: string;
}

export interface IQcEquipment {
    id: string;
    bearing: number;
    type: string;
}

export interface IQcSpanEntity extends IQcBaseEntity {
    span_id: string;
}

export interface IQcPole extends IQcBaseEntity {
    entityVersionId: string;
    poletagImageUrls: IPoletagImage[];
    poletagLoadedImageUrl: IPoletagImage;
    equipment: IQcEquipment[];
    backwardNoHeightLabels?: Array<ISvgLabelData>;
    forwardNoHeightLabels?: Array<ISvgLabelData>;
    anchors: IQcEquipment[];

    /**
     * used internally in helper functions
     */
    currentImageId?: string;
}

export type ImageDeltas = {
    imageRatio: number;
    imageWidth: number;
    imageXCorrection: number;
};

export type IProcessedQcImage = IQcBaseEntity & {
    equipment?: IQcEquipment[];
    backwardNoHeightLabels?: Array<ISvgLabelData>;
    forwardNoHeightLabels?: Array<ISvgLabelData>;
    anchors?: IQcEquipment[];
    imageDeltas: ImageDeltas;
};

export interface ISalesforceQcJob {
    id: number;
    analyst: string;
    jobName: string;
    departmentName: string;
    priority: string;
    dueDate: string;
    requestedDate: string;
    jobStatus: string;
    minimumPoles: number;
    dateAssigned: string;
    ikeAnalystPla: string;
    targetInspectionPercent: number;
    totalPoleCount: number;
    ikeAComments: string;
}

export interface IQcStatusOption {
    id: number;
    name: string;
    display_name: string;
}

export interface ISelectedPoleAttachedSpan {
    span_id: string;
    bearing: number;
    pole_2: string;
    pole_1: string;
    type?: string;
}

export const DEFAULT_TARGET_INSPECTION_RATE = 0.1;
export const DEFAULT_TARGET_YIELD_RATE = 0.8;

export type ImageIndex = 0 | 1 | 2 | 3 | 4;

export type GetSpansResponse = GetSpansPole[];

export type GetSpansPole = Pole & {
    agl_attachment_height?: AttachmentPoint;
    pole: 'selected' | 'adjacent';
    client_id: string[];
    equipments: Equipment[];
    mres: Mre[];
    anchors: Anchor[];
    pole_mid_span_photo: Image[];
    images: {
        true_size_images: Image[];
        pole_tag_images: Image[];
    };
    span: {
        selected: Span[];
    };
};

export type Pole = {
    pole_id: string;
    pole_type?: string; // to be deprecated
    species?: string;
    pole_class?: string;
    length?: string;
    owner?: string;
    street_address?: string;
    status?: string;
    note?: string;
    /**
     * foreign key for base_attachment_height_id
     */
    base_attachment_height?: string;
    /**
     * foreign key for agl_attachment_height_id
     */
    agl_attachment_height?: string;
    other_pole_attachments?: AttachmentPoint[];
    circumference?: number;
    entity_version?: string;
    /**
     * JSON field
     */
    generic_data?: IkeOfficeGenericDatum[];
};

export type Span = {
    span_id: string;
    ike_office_span_id?: string;
    /**
     * foreign key for pole_1_id
     */
    pole_1: string;
    /**
     * foreign key for pole_2_id
     */
    pole_2: string;
    provider_type?: string;
    span_type?: 'Fore Span' | 'Back Span' | 'Other' | 'Building';
    span_note?: string;
    /**
     * JSON field
     */
    span_value?: {
        pitch?: number;
        bearing?: number;
        distance?: number;
    };
    /**
     * line string
     */
    geom: string;
    span_environment?: string;
    /**
     * date time
     */
    last_modified: string;
    /**
     * JSON field
     */
    generic_data: any;

    span_mid_span_photo?: Image[];

    span_guys?: Guy[];

    wire_segments?: WireSegment[];
};

export type AttachmentPoint = {
    attachment_id: string;

    y_pixel?: number;
    x_pixel?: number;

    /**
     * Foreign key for true_size_image image_id
     */
    image: string;
    /**
     * calculated distance to the base of the pole
     */
    distance?: number;

    /**
     * optional label field for the case when the measurement isn't a wire / equipment / mre / agl or base attachment
     */
    label?: string;
    measurement_id?: string;
    reference_point?: AttachmentPoint;
    /**
     * Foreign key for pole_id
     */
    pole?: string;
    point_type?: 'point' | 'marker' | 'base';

    /**
     * only applicable to base point_type
     */
    offset?: number;
    collection_field_ids?: string[];
    collection_field_name?: string;
};

export type Equipment = {
    equipment_id: string;

    /**
     * Foreign key for pole
     */
    pole: string;

    equipment_owner?: string;
    equipment_type?: string;
    equipment_note?: string;
    equipment_orientation?: number;
    /**
     * Foreign key for equipment_attachment_height_id
     */
    equipment_attachment_height?: AttachmentPoint;

    parent_equipment_id: string;
    equipment_size?: string;
    equipment_quantity?: string;
    /**
     * Foreign key for equipment_bottom_height_id
     */
    equipment_bottom_height?: string;
    /**
     * JSON field
     */
    generic_data?: any;
};

export type Wire = {
    wire_id: string;
    wire_name?: string;
    wire_size?: string;
    wire_quantity?: number;
    wire_owner?: string;
    wire_insulator?: string;
    wire_construction?: string;
    ruling_span?: string;
    framing_type?: string;
    circuit_mid_span_height?: number;
    /**
     * JSON field
     */
    generic_data?: any;
};

export type WireSegment = {
    wire_segment_id: string;

    /**
     * Foreign key for span
     */
    span: string;

    attachment_point_1: AttachmentPoint;
    attachment_point_2: AttachmentPoint;

    /**
     * Foreign keys for attachment_point_id
     */
    attachment_point: string[]; // to be deprecated
    attachment_point_phase_a?: AttachmentPoint;
    attachment_point_phase_b?: AttachmentPoint;
    attachment_point_phase_c?: AttachmentPoint;
    /**
     * Foreign key for mid_span_height_id
     */
    mid_span_height: string;

    wire?: Wire;
    size: string; // the name of the wire segment
    /**
     * JSON field
     */
    generic_data: any;
};

export type Anchor = {
    anchor_id: string;
    /**
     * Foreign id for pole
     */
    pole: string;
    anchor_type?: string;
    anchor_owner?: string;
    anchor_note?: string;
    /**
     * Line string
     */
    anchor_lead_length?: string;
    anchor_bearing?: number;
    anchor_pitch?: number;
    anchor_distance?: number;
    /**
     * JSON field
     */
    generic_data?: any;

    guys?: Guy[];
};

export type Guy = {
    guy_id: string;
    guy_size: string;
    guy_owner?: string;
    /**
     * Foreign key for anchor_id
     */
    anchor?: string;

    guy_attachment_height?: AttachmentPoint;
    guy_attachment_height_at_wep?: AttachmentPoint;
    guy_midspan_attachment_height?: AttachmentPoint;

    guy_type?: 'Span' | 'Anchor';
    /**
     * Foreign key for Span
     */
    span?: string;
    /**
     * JSON field
     */
    generic_data?: any;
};

export type Mre = {
    mre_id: string;
    /**
     * Foreign key for Pole
     */
    pole: string;
    /**
     * JSON field
     */
    generic_data?: any;
};

export type Image = {
    image_id: string;
    capture_type: 'image' | 'truesize';
    collection_field_name: string;
    collection_field_type: string;
    collection_field: string;
    image_resolution_width: number;
    image_resolution_height: number;
    image_orientation_bearing?: number;
    image_orientation_pitch?: number;
    image_distance?: number;
    attachment_point_set: AttachmentPoint[];
    capture_id: string;
    lat: number;
    lng: number;
};

export type IkeOfficeGenericDatum = {
    field: string;
    name: string;
    type:
        | 'height'
        | 'text'
        | 'date'
        | 'location'
        | 'nestedlist'
        | 'number'
        | 'form'
        | 'selectlist'
        | 'yesno'
        | 'attachment_height'
        | 'vector';
    provider?: {
        type: 'manual' | 'attachment_point';
        attachment_point?: AttachmentPoint;
    };
    value: any;
};

export type FormGenericDatumValue = {
    fields: IkeOfficeGenericDatum[];
    id: string;
};

export function getTemplateObject(position: QcPhotoPosition) {
    switch (position) {
        case QcPhotoPosition.BACKPOLE:
        case QcPhotoPosition.FOREPOLE:
            return {
                entityVersionId: null,
                photoPosition: position,
                truesizeImageUrls: [],
                truesizeImageInfo: [],
                poletagImageUrls: [],
                backwardLabels: [],
                forwardLabels: [],
                title: null,
                equipment: [],
                anchors: [],
            };
        case QcPhotoPosition.BACKSPAN:
        case QcPhotoPosition.FORESPAN:
            return {
                photoPosition: position,
                truesizeImageUrls: [],
                truesizeImageInfo: [],
                backwardLabels: [],
                forwardLabels: [],
                title: null,
                bearing: null,
                span_id: null,
            };
        case QcPhotoPosition.CENTREPOLE:
            return {
                entityVersionId: null,
                photoPosition: QcPhotoPosition.CENTREPOLE,
                truesizeImageUrls: [],
                truesizeImageInfo: [],
                poletagImageUrls: [],
                backwardLabels: [],
                forwardLabels: [],
                backwardNoHeightLabels: [],
                forwardNoHeightLabels: [],
                title: null,
                equipment: [],
                anchors: [],
            };
    }
}
